import React, { useState } from 'react';
import * as yup from 'yup';
// components
import { Field, FormElement } from '@progress/kendo-react-form';
import { Label } from '@progress/kendo-react-labels';
import Attachments from 'shared/Attachments';
import CancelButton from 'shared/buttons/CancelButton';
import DeleteButton from 'shared/buttons/DeleteButton';
import SaveButton from 'shared/buttons/SaveButton';
import { FormInput, FormNumericTextBox, FormSelect } from 'shared/formControls';
import BaseForm from 'shared/forms/BaseForm';
import { SingleEditGridInput as GridInput } from 'shared/forms/GridInput';
import { BaseCell } from 'shared/grids/Cells';
import { Box } from 'shared/layout/Box';
import { useToast } from 'shared/toast';
import { deleteTruckPermitRule, deleteTruckPermitRuleState, upsertTruckPermitRuleState } from 'src/fetch/trucks';
import CountiesMultiSelect from './components/CountiesMultiSelect';
import { ConfirmDeleteDialogue } from 'shared/modals/ConfirmDialogue/ConfirmDialogue';

const TEXAS_STATE_ID = 51;
const TRUCK_PERMIT_DOCUMENT_TYPE = -106;

const permitRuleSchema = yup.object().shape({
  name: yup.string().nullable().required('Name is required'),
  maxGrossWeight: yup.number().nullable().required('Max Gross Weight is required').min(0, 'Max Gross Weight must be greater than or equal to 0'),
  type: yup.mixed().nullable().required('Truck Permit Type is required'),
  files: yup.array().nullable().when('id', {
    is: (id) => !id, // Only validate files if id is null or undefined
    then: yup.array().min(1, 'Upload at least one file').required('Upload at least one file'),
    otherwise: yup.array().nullable(), // No validation if id is present
  }),
});

const defaultValues = {
  id: 0,
  name: '',
  deleted: false,
  truckPermitStates: [],
  truckPermitType: 0,
};

const newState = {
  state: null,
  truckPermitCounties: [],
  inEdit: true,
  isNew: true,
};

const TruckPermitRuleForm = (props) => {
  const { initialValues, onSubmit } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = (values) => {
    setIsSubmitting(true);
    onSubmit({
      ...(values ?? {}),
      type: values?.type?.value ?? values?.type,
      files: values?.files?.map((file) => ({
        ...(file ?? {}),
        documentTypeId: TRUCK_PERMIT_DOCUMENT_TYPE,
        truckPermitRuleId: values?.id,
        fileId: file.id,
        file,
      })),
    }).finally(() => setIsSubmitting(false));
  };

  return (
    <>
      <BaseForm
        id="truck-permit-rule-form"
        initialValues={{ ...defaultValues, ...(initialValues ?? {}) }}
        validationSchema={permitRuleSchema}
        onSubmit={handleSubmit}
        render={(p) => <RenderForm {...p} {...props} isSubmitting={isSubmitting} />}
      />
    </>
  );
};

const emptyArray = [];
const emptyObject = {};

const RenderForm = ({
  onChange,
  allowSubmit,
  valueGetter,
  errors,
  touched,
  initialValues = emptyObject,
  onCancel,
  states,
  truckPermitRuleTypes,
  forceReload,
  isSubmitting,
}) => {
  const toast = useToast();

  const truckPermitStates = valueGetter('truckPermitStates') ?? emptyArray;
  const count = truckPermitStates.length;
  const ruleIsNew = !initialValues.id;

  const usedStates = (ruleIsNew ? truckPermitStates : initialValues.truckPermitStates)?.map((s) => s.state?.state ?? s.state);
  const options = states
    .slice()
    .sort(({ value: valueA }, { value: valueB }) => {
      if (valueA === TEXAS_STATE_ID) {
        return -1;
      }
      if (valueB === TEXAS_STATE_ID) {
        return 1;
      }
      return 0;
    })
    .map(({ value, description }) => ({ state: value, stateName: description }));
  const filterOptions = React.useCallback((value) => options.filter((state) => state.state === (value.state ?? value) || !usedStates.includes(state.state)), [states, JSON.stringify(usedStates)]);

  const handleDelete = () => {
    deleteTruckPermitRule(initialValues.id)
      .then(() => {
        forceReload();
        onCancel();
      })
      .catch((error) => {
        console.error(error);
        if (error?.message?.toLowerCase()?.includes('cancelled by user')) return;
        toast.error({ message: 'Delete Failed' });
      });
  };

  const onDeleteStateCounties = async (dataItem) => {
    if (initialValues.id) {
      try {
        await deleteTruckPermitRuleState(initialValues.id, dataItem.state);
        forceReload();
        toast.success({ message: 'State and County Deleted Successfully' });
      } catch (error) {
        console.error(error);
        if (error?.message?.toLowerCase()?.includes('cancelled by user')) throw error;
        toast.error({ message: 'Delete Failed' });
        throw error;
      }
    }
  };

  const onEnterEdit = (dataItem, v) => {
    const updated = truckPermitStates.map((item) => (item.state === dataItem.state ? { ...item, inEdit: true, initalValue: item } : item));
    onChange('truckPermitStates', { value: updated });
  };

  const handleSubmitCountyState = async (dataItem) => {
    const state = dataItem.state?.state ?? dataItem.state;
    if (!state) {
      return toast.error({ message: 'State is required' });
    }
    const stateName = dataItem.state?.stateName ?? dataItem.stateName;
    const rowIndex = truckPermitStates.findIndex(({ state }) => state === dataItem.state);
    const updated = { ...dataItem, state, stateName, inEdit: false, isNew: false };
    if (!ruleIsNew) {
      try {
        await upsertTruckPermitRuleState(initialValues.id, updated);
        forceReload();
      } catch (error) {
        console.error(error);
        toast.error({ message: 'Save Failed' });
        return;
      }
    }
    const updatedList = [...truckPermitStates];
    updatedList[rowIndex] = updated;

    onChange('truckPermitStates', { value: updatedList });
    toast.success({ message: 'State and County Saved Successfully' });
  };

  const stateColumns = React.useMemo(() => [
    {
      title: 'State',
      field: 'state',
      cell: ({ dataIndex, dataItem }) => (
        <BaseCell style={{ paddingTop: '1.75em' }}>
          {!dataItem?.isNew ? dataItem.stateName : (
            <Field
              disabled={!dataItem.inEdit}
              component={FormSelect}
              id={`truckPermitStates.${dataIndex}.state`}
              name={`truckPermitStates.${dataIndex}.state`}
              data={filterOptions(dataItem)}
              dataItemKey="state"
              textField="stateName"
              value={dataItem.state}
              onChange={(e) => {
                const updated = truckPermitStates.map((item) => (item.state === e.value ? { ...item, state: e.value, stateName: e.value.stateName, truckPermitCounties: [] } : item));
                onChange('truckPermitStates', { value: updated });
              }}
              showValidationMessage={false}
            />
          )}
        </BaseCell>
      ),
    },
    {
      title: 'Counties',
      field: 'truckPermitCounties',
      cell: ({ dataIndex, dataItem, ...props }) => {
        const countiesWrapperStyle = {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          width: '100%',
          minWidth: '200px',
          marginTop: '1.75em',
        };
        return (
          <BaseCell>
            {dataItem.inEdit ? (
              <CountiesMultiSelect
                {...props}
                wrapperStyle={countiesWrapperStyle}
                placeholder=" All"
                stateId={dataItem.state?.state ?? dataItem.state}
                showValidationMessage={false}
              />
            ) : dataItem.truckPermitCounties.length > 0 ? (
              dataItem.truckPermitCounties.map(({ countyName }) => countyName).join(', ')
            ) : (
              <Box {...countiesWrapperStyle}>
                All
              </Box>
            )}
          </BaseCell>
        );
      },
    },
  ], [!!options, truckPermitStates]);

  const onCancelStateEdit = (dataItem, index) => {
    if (dataItem?.isNew) {
      return onChange('truckPermitStates', { value: truckPermitStates.filter(({ state }) => state !== dataItem.state) });
    }
    const updated = [...truckPermitStates];
    if (!dataItem.initalValue) { throw new Error('No initial value found for state'); }
    updated[index] = { ...updated[index].initalValue, inEdit: false };
    onChange('truckPermitStates', { value: updated });
  };

  return (
    <FormElement>
      <fieldset className="k-form-fieldset">
        <Box display="flex" flexDirection="row" alignItems="flex-start" paddingTop="10px" justifyContent="flex-start" gap="10px">
          <Field id="name" name="name" label="Name" component={FormInput} />
          <Field
            id="maxGrossWeight"
            name="maxGrossWeight"
            label="Max Gross Weight"
            component={FormNumericTextBox}
          />
          <Field
            id="type"
            name="type"
            label="Truck Permit Type"
            component={FormSelect}
            data={truckPermitRuleTypes}
            dataItemKey="value"
            textField="description"
          />
        </Box>
        <div style={{ marginTop: '1em' }}>
          <Label>States and Counties</Label>
          <Field
            resultCount={count}
            id="truckPermitStates"
            name="truckPermitStates"
            component={GridInput}
            defaultRowValue={newState}
            columns={stateColumns}
            editField='inEdit'
            ActionCellProps={{
              itemName: 'this item',
              useHandleRowSubmit: true,
              handleRowSubmit: handleSubmitCountyState,
              onEnterEdit,
              onDelete: onDeleteStateCounties,
              onCancel: onCancelStateEdit,
            }}
          />
        </div>
        <div className="col--12">
          <Attachments
            source="truckPermitRule"
            sourceId={initialValues.id}
            defaultDocumentType={TRUCK_PERMIT_DOCUMENT_TYPE}
            hideUploadAction={ruleIsNew}
            onChange={(files) => {
              onChange('files', { value: files });
            }}
          />
          {errors.files && touched && <div className="k-form-error">{errors.files}</div>}
        </div>
        <div className="k-form-buttons">
          <SaveButton disabled={!allowSubmit || isSubmitting} style={{ marginRight: '.5em' }} />
          {initialValues?.id && <DeleteButton disabled={isSubmitting} onClick={handleDelete} />}
          <CancelButton onClick={onCancel} />
        </div>
      </fieldset>
    </FormElement>
  );
};

export default TruckPermitRuleForm;
